import { useEffect, useRef } from 'react';
import {
  init as sentryInit,
  close as sentryClose,
  replayIntegration,
  reactRouterV6BrowserTracingIntegration,
} from '@sentry/react';
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router-dom';

import { useFeatureFlags } from '@/hooks/featureFlag/useFeatureFlags';
import { useConfig } from '@/hooks/shared/useConfig';
import { useAuthentication } from '@/hooks/user/useAuthentication';

export const useSentryLoader = () => {
  const { isSentryEnabled } = useFeatureFlags();
  const { config } = useConfig();
  const { user } = useAuthentication();
  const hasInitedRef = useRef(false);
  const prevEnabledRef = useRef(false);
  const isSentryActive = isSentryEnabled && Boolean(user);

  useEffect(() => {
    const dsn = config.VITE_SENTRY_DSN;

    if (isSentryActive) {
      if (!hasInitedRef.current && dsn) {
        // First enable on this page load:
        // initialize the Sentry SDK once and remember that this page already initialized it.
        sentryInit({
          dsn,
          environment: config.VITE_SENTRY_ENVIRONMENT,
          integrations: [
            reactRouterV6BrowserTracingIntegration({
              useEffect,
              useLocation,
              useNavigationType,
              createRoutesFromChildren,
              matchRoutes,
            }),
            replayIntegration({
              maskAllText: false,
              blockAllMedia: false,
            }),
          ],
          tracesSampleRate: 1,
          replaysSessionSampleRate: 0.1,
          replaysOnErrorSampleRate: 1.0,
        });
        hasInitedRef.current = true;
      } else if (hasInitedRef.current && !prevEnabledRef.current) {
        // Flag changed from OFF -> ON after Sentry had already been closed before.
        // Re-initializing the SDK in place after Sentry.close() is not reliable,
        // so the safe recovery path is a full page reload. On the fresh page load,
        // this hook will run again, see the flag is ON, and call sentryInit() from
        // the first branch above.
        window.location.reload();
        return;
      }
    } else if (prevEnabledRef.current && hasInitedRef.current) {
      // Flag changed from ON -> OFF (or user logged out) after Sentry was already
      // initialized in this tab. Close the client to flush any pending events and
      // tear down the transport so no further network calls are made to Sentry.
      // This is required for GDPR: opted-out customers must see zero Sentry traffic.
      sentryClose();
    }

    prevEnabledRef.current = isSentryActive;
  }, [config.VITE_SENTRY_DSN, config.VITE_SENTRY_ENVIRONMENT, isSentryActive]);
};
